import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import React, { ReactNode, useMemo } from 'react'
import Group from 'components/utils/Group'
import LeHotelCartReviewTile from 'components/Pages/CartReviewPage/LeHotelCartReviewTile'
import LeHotelCartReviewGroup from 'components/Pages/CartReviewPage/LeHotelCartReviewGroup'
import BedbankCartReviewTile from 'components/Pages/CartReviewPage/BedbankCartReviewTile'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  isBedbankItem,
  isExperienceItem,
  isTransferItem,
  isLuxPlusSubscriptionItem,
  isSubscriptionJoinItem,
  isFlightItem,
  isInstantBookingLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import ExperienceCartReviewTile from 'components/Pages/CartReviewPage/ExperienceCartReviewTile'
import AirportTransferCartReviewTile from 'components/Pages/CartReviewPage/AirportTransferCartReviewTile'
import FlightCartReviewTile from 'components/Pages/CartReviewPage/FlightCartReviewTile'
import LuxPlusCartReviewTile from 'components/Pages/CartReviewPage/LuxPlusCartReviewTile'
import { CartItemData } from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { groupBy } from 'lib/array/arrayUtils'
import BedbankCartReviewGroup from 'components/Pages/CartReviewPage/BedbankCartReviewGroup'
import UserCartComparisonBanner from './UserCartComparisonBanner'

const StyledHeadingTextBlock = styled(HeadingTextBlock)`
  padding-left: ${rem(16)};
  padding-bottom: ${rem(4)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

const StyledGroup = styled(Group)`
  margin-bottom: ${rem(12)};
`

interface Props {
  items: Array<App.Checkout.AnyItem>
  title?: string
  icon?: ReactNode
  cartItemsData: Array<CartItemData>
  tileVariant?: 'default' | 'condensed';
  id?: string;
}

function UserCartSection(props: Props) {
  const {
    items,
    title,
    icon,
    cartItemsData,
    tileVariant = 'default',
    id,
  } = props

  const itemGroups = useMemo(() => {
    return groupBy(items, (item) => isBedbankItem(item) || isInstantBookingLEHotelItem(item) ? `${item.offerId}-${item.checkIn}-${item.checkOut}` : item.itemId)
  }, [items])

  return <StyledGroup gap={12} tabletGap={tileVariant === 'condensed' ? 4 : 20} direction="vertical" horizontalAlign="stretch">
    {title && icon && <StyledHeadingTextBlock startIcon={icon} variant="heading5">{title}</StyledHeadingTextBlock>}
    {id === 'accommodation' && <UserCartComparisonBanner />}
    {Array.from(itemGroups.entries()).map(([key, value]) => {
      // Handle multi-room groups
      if (value.length > 1) {
        if (value.every(item => isBedbankItem(item))) {
          return <BedbankCartReviewGroup
            key={key}
            items={value}
            tileVariant={tileVariant}
          />
        } else if (value.every(item => isInstantBookingLEHotelItem(item))) {
          return <LeHotelCartReviewGroup
            key={key}
            items={value}
            tileVariant={tileVariant}
          />
        }
      }

      const item = value[0]

      if (isLuxPlusSubscriptionItem(item)) {
        return <LuxPlusCartReviewTile
          key={item.itemId}
          item={item}
          cartItemsData={cartItemsData}
          tileVariant={tileVariant}
        />
      } else if (isSubscriptionJoinItem(item)) {
        // Joining fee be shown within LuxPlusUserCartItem, no need for a separate tile
        return null
      } else if (isInstantBookingLEHotelItem(item)) {
        return <LeHotelCartReviewTile
          key={item.itemId}
          item={item}
          tileVariant={tileVariant}
        />
      } else if (isBedbankItem(item)) {
        return <BedbankCartReviewTile
          key={item.itemId}
          item={item}
          tileVariant={tileVariant}
        />
      } else if (isFlightItem(item)) {
        return <FlightCartReviewTile
          key={item.itemId}
          item={item}
          tileVariant={tileVariant}
        />
      } else if (isExperienceItem(item)) {
        return <ExperienceCartReviewTile
          key={item.itemId}
          item={item}
          tileVariant={tileVariant}
        />
      }
      else if (isTransferItem(item)) {
        return <AirportTransferCartReviewTile
          key={item.itemId}
          item={item}
          tileVariant={tileVariant}
        />
      }
      return null
    })}
  </StyledGroup>
}

export default UserCartSection
